import * as React from 'react';
import AdminUpgradeFeatures from 'admin/components/AdminUpgradeFeatures';
import AdminUpgradePlanQuoteReviewBox from 'admin/components/AdminUpgradePlan/AdminUpgradePlanQuoteReviewBox';
// @ts-ignore
import AdminUpgradeQuoteTotalBox from 'admin/components/AdminUpgradeQuoteTotalBox';
// @ts-ignore
import AdminUpgradeShareQuoteBanner from 'admin/components/AdminUpgradeShareQuoteBanner';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import { CardElement } from 'react-stripe-elements';
import Checkbox from 'components/Checkbox';
import Columns2 from 'components/Columns2';
// @ts-ignore
import Config from 'Config';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
// @ts-ignore
import InputValueConstants from 'constants/InputValueConstants';
import Label from 'components/Label';
import LinkConstants from 'constants/LinkConstants';
import MailTo from 'components/MailTo';
import Row from 'components/Row';
import Text from 'components/Text';
import Time from 'components/Time';
import { UpgradePlanData, UpgradePlanQuote } from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';

import '../styles/AdminUpgradePlanPaymentForm.scss';

type Props = {
  requesterFirstName?: string;
  requesterLastName?: string;
  role?: string;
  phoneNumber?: string;
  requesterEmail?: string;
  quote?: UpgradePlanQuote;
  orgName?: string;
  orgAddress?: string;
  orgType?: 'School' | 'District';
  startDate?: string;
  endDate?: string;
  price?: string;
  taxExempt?: boolean;
  tax?: string,
  onAdvance: () => void;
  onCardInputCompleted: () => void;
  onContactSales: () => void;
  onPersistState: (fields: UpgradePlanData, onSuccess: Function) => void;
};

type State = {
  billingFirstName?: string;
  billingLastName?: string;
  billingEmail?: string;
  useRequesterAsBillingContact?: boolean;
  adminVerificationUrl?: string;
};

class AdminUpgradePlanPaymentForm extends React.Component<Props, State> {
  trackingPrefix = 'admin_self_serve.payment_form';

  state: State = {
    useRequesterAsBillingContact: true,
  };

  componentDidMount() {
    this.track('view');
  }

  track = (key: string) => {
    EventTracking.trackKey(`${this.trackingPrefix}.${key}`);
  };

  getAdminVerificationLabel = () => {
    const { requesterEmail, role, orgName } = this.props;

    return `To turn on paid features, provide an official website (e.g. a staff directory) ` +
      `that shows ${requesterEmail} as the ${role} at ${orgName}. If you need to verify another way, contact sales`;
  };

  renderCardForm = () => {
    const { orgName, taxExempt, onCardInputCompleted } = this.props;
    const subject = `Tax Exemption for ${orgName}`;

    return (
      <div>
        {taxExempt && (
          <Text element="div" size="medium" className="AdminUpgradePaymentForm-featuresNotice">
            Email your tax exemption form to{' '}
            {
              <MailTo subject={subject} to={LinkConstants.BILLING_EMAIL}>
                {LinkConstants.BILLING_EMAIL}
              </MailTo>
            }{' '}
            with your name and your school/district name.
          </Text>
        )}
        <Row>
          <Label>Credit or debit card</Label>
          <CardElement
            className="AdminUpgradePlanPaymentForm-cardInput"
            onChange={(e: stripe.elements.ElementChangeResponse) => {
              if (e.complete && !e.error) {
                onCardInputCompleted();
              }
            }}
            onFocus={() => this.track('credit_card_input.click')}
          />
        </Row>
      </div>
    );
  };

  renderAdminVerificationForm = () => {
    return (
      <FormElement
        label="Administrator verification"
        labelNote={this.getAdminVerificationLabel()}
        validate={e => {
          assertions.url(e.target.value);
          assertions.notEmpty(e.target.value);
        }}
        onValidChange={e =>
          this.setState({ adminVerificationUrl: e.target.value })
        }
        onInvalidChange={() =>
          this.setState({ adminVerificationUrl: undefined })
        }
        element={Input}
        elementProps={{
          defaultValue: this.state.adminVerificationUrl,
          onFocus: () => this.track('admin_verification_url.click'),
        }}
      />
    );
  };

  renderBillingContactDetailsForm = () => {
    return (
      <React.Fragment>
        <Columns2 className="AdminUpgradePlanPaymentForm-content-columns" reset="mobile">
          <FormElement
            label="First name"
            validate={e => {
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.FIRST_NAME_LENGTH
              );
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e =>
              this.setState({ billingFirstName: e.target.value })
            }
            onInvalidChange={() =>
              this.setState({ billingFirstName: undefined })
            }
            element={Input}
            elementProps={{
              defaultValue: this.state.billingFirstName,
              onFocus: () => this.track('billing_first_name.click'),
            }}
          />
          <FormElement
            label="Last name"
            validate={e => {
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.LAST_NAME_LENGTH
              );
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e =>
              this.setState({ billingLastName: e.target.value })
            }
            onInvalidChange={() =>
              this.setState({ billingLastName: undefined })
            }
            element={Input}
            elementProps={{
              defaultValue: this.state.billingLastName,
              onFocus: () => this.track('billing_last_name.click'),
            }}
          />
        </Columns2>
        <Row>
          <FormElement
            label="Work email address"
            validate={e => {
              assertions.email(e.target.value);
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e =>
              this.setState({ billingEmail: e.target.value })
            }
            onInvalidChange={() =>
              this.setState({ billingEmail: undefined })
            }
            element={Input}
            elementProps={{
              defaultValue: this.state.billingEmail,
              onFocus: () => this.track('billing_email.click'),
            }}
          />
        </Row>
      </React.Fragment>
    );
  };

  renderProgressionButtons = () => {
    const { onPersistState, onAdvance, onContactSales } = this.props;

    return (
      <Buttons align="left">
        <Button onClick={() => onPersistState(this.state as UpgradePlanData, onAdvance)}>
          Purchase
        </Button>
        <Button level="secondary" onClick={() => onPersistState(this.state as UpgradePlanData, onContactSales)}>
          Contact sales
        </Button>
      </Buttons>
    );
  };

  render() {
    const { orgName, startDate, endDate, quote } = this.props;
    const { useRequesterAsBillingContact } = this.state;

    return (
      <div className="AdminUpgradePlanPaymentForm">
        <div className="AdminUpgradePlanPaymentForm-content">
          <div className="AdminUpgradePlanPaymentForm-content-body">
            <div className="AdminUpgradePlanPaymentForm-content-body-forms">
              <Row>
                <Text weight="semibold" size="xlarge" element="div">
                  Review your Remind plan
                </Text>
                <Text size="medium" element="div">
                  Enter your payment details to complete your purchase.
                </Text>
              </Row>
              <Row>
                <AdminUpgradePlanQuoteReviewBox
                  orgName={orgName ?? ''}
                  startDate={startDate ?? ''}
                  endDate={endDate ?? ''}
                />
                <AdminUpgradeQuoteTotalBox quote={quote} />
              </Row>
              <Row>
                <AdminUpgradeShareQuoteBanner
                  shareToken={quote?.shareToken}
                  showToast={false}
                  orgName={orgName}
                />
              </Row>
              <Row>
                {this.renderCardForm()}
              </Row>
              <div>
                <Row>
                  <Checkbox
                    checked={useRequesterAsBillingContact}
                    onChange={() => this.setState({ useRequesterAsBillingContact: !useRequesterAsBillingContact })}
                    label={'Use same name and email for billing'}
                  />
                </Row>
                {!useRequesterAsBillingContact && this.renderBillingContactDetailsForm()}
                <Row>
                  {this.renderAdminVerificationForm()}
                </Row>
              </div>
              {this.renderProgressionButtons()}
            </div>
            <div className="AdminUpgradePlanPaymentForm-content-body--desktop-side">
              <Row size="xxxlarge">
                <AdminUpgradeFeatures />
              </Row>
            </div>
          </div>
        </div>
        <div className="AdminUpgradePlanPaymentForm--mobile-bottom">
          <div className="AdminUpgradePlanPaymentForm-info-container">
            <AdminUpgradeFeatures />
          </div>
        </div>
      </div>
    );
  }
}

export { AdminUpgradePlanPaymentForm };
export default AdminUpgradePlanPaymentForm;
